/* The notification list shown when the bell is opened */
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Col, Hr, Row, View } from '@amzn/stencil-react-components/layout';
import { Link } from '@amzn/stencil-react-components/link';
import { Modal, ModalBody, ModalContainer, ModalFooter, ModalHeader } from '@amzn/stencil-react-components/modal';
import { Pagination } from '@amzn/stencil-react-components/pagination';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import { UtilityButton } from '@amzn/stencil-react-components/utility-button';
import { H3, Text, TextVariant } from '@amzn/stencil-react-components/text';
import IconCrossSmall from '@amzn/stencil-react-icons/icons/icon-cross-small';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Status } from 'src/models/AuthContextModels';
import { AppDispatch, RootState } from 'src/store/store';
import { navigateWithEmbedParams } from 'src/utils/navigation-utils';
import { selectActiveBusinessGroups } from 'src/features/business-group/businessGroupSelectors';
import { isActionOutstanding, isDismissible, isUnread, prioritizeActionable, XptNotification } from './notificationModels';
import { formatPageSummary } from './notificationPagination';
import { getNotificationPresentation, resolveNotificationPath } from './notificationPresentation';
import { markNotificationsReadThunk } from './notificationsSlice';
import { getSeverityStencilPresentation, ICON_COLUMN_WIDTH } from './notificationStencilPresentation';
import { useDismissedNotifications } from './useDismissedNotifications';
import { useNotificationPagination } from './useNotificationPagination';

/** Id linking the modal to its title, required for an accessible label on a controlled Modal. */
const MODAL_TITLE_ID = 'notification-panel-title';

/**
 * FIXED modal width, wider than ModalContainer's 544px default because rows carry an icon, a
 * title and a full sentence.
 *
 * ModalContainer only accepts maxWidth, and Modal renders the dialog as a flex item with no width
 * of its own — so it shrinks to fit its content and maxWidth merely caps it, which is why the
 * width shifted as content changed. Setting the width on the container's child fixes the content
 * box, which the shrink-to-fit parent then adopts. maxWidth 100% on that child keeps it from
 * overflowing viewports narrower than this.
 */
const MODAL_WIDTH = '720px';

/**
 * MINIMUM height for the list — not a fixed height, and never a scroll container.
 *
 * The page size caps the list at five rows, so there is nothing to scroll to; the list is allowed
 * to grow if messages wrap further than expected. The minimum keeps a part-full last page, the
 * empty state and the loading state from collapsing the modal to a different size than a full
 * page, which is what made it appear to change scale while paging.
 */
const LIST_MIN_HEIGHT = '440px';

/**
 * Font sizes for the panel, in absolute units.
 *
 * Deliberately NOT the `text.size.*` tokens: those resolve to `rem`, so their rendered size
 * depends on the document's root font size, and every attempt at sizing this panel through
 * them came out smaller on screen than the token value implied. Absolute values render at the
 * stated size regardless of the surrounding page, which is what a design spec asks for.
 *
 * `shouldUseFixedFontStyle` on each Text keeps the declaration out of a media query, so the
 * size also holds at every viewport width rather than shifting per breakpoint. Variants are
 * still set, for font family and for intent.
 */
const HEADER_FONT_SIZE = '24px';
/**
 * Button labels are sized by Stencil from the rem-based `text.size.200` token, which renders
 * smaller here than the token implies and left Close looking like plain text rather than a
 * button. Button exposes no fontSize prop, so the label is wrapped in a Text pinned to px, at
 * the medium weight ButtonBase already applies.
 */
const BUTTON_FONT_SIZE = '16px';
const TITLE_FONT_SIZE = '16px';
const BODY_FONT_SIZE = '15px';
const META_FONT_SIZE = '14px';
const COUNT_FONT_SIZE = '14px';
/*
 * Pagination page numbers are sized in components.scss under .notification-pagination. Each page
 * is a <button>, and stencil-reset.css leaves `button` out of its `font: inherit` element list, so
 * form controls keep the user-agent font (~13px) and no wrapper font size can reach them.
 */

/**
 * Unread-count pill, built here rather than with Stencil's Badge.
 *
 * Badge renders its value at a hardcoded label-xs inside a fixed 16px pill and exposes no size
 * prop, which left the number too small to read next to a 24px heading. This keeps Badge's
 * appearance — the same vibrant blue fill, inverted text and fully rounded pill — at a legible
 * size. Sized with a minimum rather than a fixed width so a 3-digit count still fits.
 */
const COUNT_PILL_MIN_SIZE = '24px';

/**
 * Row divider color, resolved to a CSS variable up front.
 *
 * Hr is a legacy-theme component: it builds `border-top: 1px solid ${colorPropAdapter(color)}`
 * and never looks the value up as a design token. Handing it a token name produces invalid CSS,
 * the browser drops the declaration, and the divider silently disappears. `token()` returns the
 * `var(--st-dt-*)` reference, which is valid CSS and still follows the theme.
 */
const DIVIDER_COLOR = token('color.border.primary');

interface NotificationPanelProps {
  visible: boolean;
  onDismiss: () => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ visible, onDismiss }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const allItems = useSelector((state: RootState) => state.notificationsStore.items);
  const status = useSelector((state: RootState) => state.notificationsStore.status);
  const activeBusinessGroups = useSelector(selectActiveBusinessGroups);

  // Business-group routes are keyed by short description, but a notification payload carries the
  // numeric id, so reviewer targets are resolved through the store.
  const resolveBusinessGroupShortDesc = useCallback(
    (businessGroupId: number) =>
      activeBusinessGroups.find((group) => group.data_classification.data_classification_id === businessGroupId)?.data_classification
        .data_classification_short_description,
    [activeBusinessGroups]
  );

  // Dismissed items are hidden everywhere in the panel — list, counts and paging.
  const { visibleItems: items, dismiss: dismissNotification } = useDismissedNotifications(allItems);

  // Requests still awaiting action lead the list, so they cannot be pushed onto a later page by
  // newer informational outcomes.
  const orderedItems = useMemo(() => prioritizeActionable(items), [items]);

  const { page, selectPage, resetToFirstPage } = useNotificationPagination(orderedItems);

  // Reopening the panel should start at the newest notifications, not wherever the user
  // last paged to.
  useEffect(() => {
    if (visible) {
      resetToFirstPage();
    }
  }, [visible, resetToFirstPage]);

  const unreadCount = items.filter(isUnread).length;

  /**
   * Advance the read watermark when the panel closes, not when it opens.
   *
   * With no "Mark all as read" control, closing the panel is what clears the unread state —
   * otherwise the bell would keep its count forever. Doing it on close rather than on open
   * means the user still sees which rows were new while the panel is in front of them.
   *
   * The watermark cannot clear an item that still represents an outstanding action, so it is
   * only dispatched when there is something it can actually clear.
   */
  const closePanel = () => {
    const hasMarkableUnread = items.some((notification) => !notification.is_read && !isActionOutstanding(notification));
    if (hasMarkableUnread) {
      dispatch(markNotificationsReadThunk());
    }
    onDismiss();
  };

  const onOpenRoute = (route: string) => {
    closePanel();
    navigateWithEmbedParams(navigate, route);
  };

  const renderRow = (notification: XptNotification) => {
    const presentation = getNotificationPresentation(notification);
    const severity = getSeverityStencilPresentation(presentation.severity);
    const unread = isUnread(notification);
    const { Icon: SeverityIcon } = severity;
    const path = resolveNotificationPath(presentation.target, resolveBusinessGroupShortDesc);

    return (
      <Row
        alignItems="flex-start"
        gridGap="dimensions.spacing.300"
        padding="dimensions.spacing.300"
        className={path ? 'notification-row' : undefined}
        onClick={path ? () => onOpenRoute(path) : undefined}
      >
        {/* Fixed-width column so every row's text starts on the same line. */}
        <Col alignItems="center" justifyContent="center" flexShrink={0} width={ICON_COLUMN_WIDTH} minWidth={ICON_COLUMN_WIDTH}>
          <SeverityIcon color={severity.iconColor} aria-hidden={true} />
        </Col>

        <Col flex={1} gridGap="dimensions.spacing.50">
          {/* Unread titles are bold, read ones regular — the weight is the only "new" marker,
              which keeps every row the same height and shape. */}
          <Row alignItems="baseline" gridGap="dimensions.spacing.200">
            {/* The title is the row's real link: the row-wide click is a convenience for mouse
                users, but keyboard and assistive-technology users need a focusable target, and
                :focus-within highlights the row when it is reached. Typography lives on the inner
                Text because Link does not apply these variants itself. */}
            {path ? (
              <Link
                href={path}
                onClick={(event) => {
                  // The row handler would fire too; stop it so navigation happens once.
                  event.preventDefault();
                  event.stopPropagation();
                  onOpenRoute(path);
                }}
              >
                <Text
                  variant={TextVariant.LabelMD}
                  fontSize={TITLE_FONT_SIZE}
                  fontWeight={unread ? 'bold' : 'regular'}
                  shouldUseFixedFontStyle={true}
                >
                  {presentation.title}
                </Text>
              </Link>
            ) : (
              <Text
                variant={TextVariant.LabelMD}
                fontSize={TITLE_FONT_SIZE}
                fontWeight={unread ? 'bold' : 'regular'}
                color="color.text.primary"
                shouldUseFixedFontStyle={true}
              >
                {presentation.title}
              </Text>
            )}
          </Row>

          <Text variant={TextVariant.BodyMD} fontSize={BODY_FONT_SIZE} color="color.text.secondary" shouldUseFixedFontStyle={true}>
            {presentation.message}
          </Text>
        </Col>

        {/* Only informational outcomes can be dismissed; a persistent item is an action still
            pending, so hiding it would lose the task. Those rows carry no control at all rather
            than a disabled one — an X that cannot be clicked reads as broken. */}
        {isDismissible(notification) && (
          <UtilityButton
            size={ButtonSize.Small}
            icon={<IconCrossSmall title={`Dismiss: ${presentation.title}`} />}
            onClick={(event) => {
              // Dismissing must not also navigate via the row's click handler.
              event.stopPropagation();
              dismissNotification(notification.notification_id);
            }}
          />
        )}
      </Row>
    );
  };

  // Only the FIRST load (no items yet) shows a spinner; a background re-fetch keeps the current list.
  const isInitialLoad = status === Status.Loading && items.length === 0;

  const renderMessage = (message: string) => (
    <Row alignItems="center" justifyContent="center" minHeight={LIST_MIN_HEIGHT}>
      <Text variant={TextVariant.BodyMD} fontSize={BODY_FONT_SIZE} color="color.text.secondary" shouldUseFixedFontStyle={true}>
        {message}
      </Text>
    </Row>
  );

  /** Fills the fixed-height list area, so empty, loading and loaded states are all the same size. */
  const renderListArea = () => {
    if (isInitialLoad) {
      return (
        <Row alignItems="center" justifyContent="center" minHeight={LIST_MIN_HEIGHT}>
          <Spinner />
        </Row>
      );
    }
    if (status === Status.Failed && items.length === 0) {
      return renderMessage('Unable to load notifications');
    }
    if (items.length === 0) {
      return renderMessage('No notifications');
    }

    return (
      <Col gridGap="dimensions.spacing.none">
        {page.pageItems.map((notification, index) => (
          <React.Fragment key={notification.notification_id}>
            {index > 0 && <Hr color={DIVIDER_COLOR} />}
            {renderRow(notification)}
          </React.Fragment>
        ))}
      </Col>
    );
  };

  return (
    <Modal isOpen={visible} close={closePanel} isScrollable={false} aria-labelledby={MODAL_TITLE_ID}>
      <ModalContainer maxWidth={MODAL_WIDTH}>
        <View width={MODAL_WIDTH} maxWidth="100%">
          {/* No onCloseButtonClick, so ModalHeader renders no X — closing is the footer button
            (plus Escape and clicking outside, which Modal handles). Both routes go through
            closePanel so the read watermark advances either way. */}
          <ModalHeader titleId={MODAL_TITLE_ID}>
            <Row alignItems="center" gridGap="dimensions.spacing.200">
              {/* Pinned like the row text, and a step above the row titles so the header
                still reads as the heading now that rows are larger. */}
              <H3 variant={TextVariant.HeadingLG} fontSize={HEADER_FONT_SIZE} shouldUseFixedFontStyle={true}>
                Notifications
              </H3>
              {unreadCount > 0 && (
                <View
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  minWidth={COUNT_PILL_MIN_SIZE}
                  height={COUNT_PILL_MIN_SIZE}
                  padding={{ left: 'dimensions.spacing.100', right: 'dimensions.spacing.100' }}
                  borderRadius="dimensions.border.radius.round"
                  backgroundColor="color.fill.vibrant.blue"
                  aria-label={`${unreadCount} unread`}
                >
                  <Text fontSize={COUNT_FONT_SIZE} fontWeight="bold" color="color.text.primary-inverted" shouldUseFixedFontStyle={true}>
                    {unreadCount}
                  </Text>
                </View>
              )}
            </Row>
          </ModalHeader>

          <ModalBody>
            <Col gridGap="dimensions.spacing.300">
              <View minHeight={LIST_MIN_HEIGHT}>{renderListArea()}</View>

              {/* Always rendered, so the footer row never appears and disappears under the list. */}
              <Row alignItems="center" justifyContent="space-between" gridGap="dimensions.spacing.200">
                {/* BodyHint is the italic, de-emphasised variant the design uses for this line. */}
                <Text variant={TextVariant.BodyHint} fontSize={META_FONT_SIZE} color="color.text.secondary" shouldUseFixedFontStyle={true}>
                  {formatPageSummary(page)}
                </Text>
                {page.numberOfPages > 1 && (
                  <View className="notification-pagination">
                    <Pagination
                      numberOfPages={page.numberOfPages}
                      selectedPage={page.currentPage}
                      onPageSelect={selectPage}
                      aria-label="Notification page selection"
                    />
                  </View>
                )}
              </Row>
            </Col>
          </ModalBody>

          <ModalFooter>
            <Row justifyContent="flex-end" alignItems="center">
              <Button variant={ButtonVariant.Secondary} onClick={closePanel}>
                <Text fontSize={BUTTON_FONT_SIZE} fontWeight="medium" shouldUseFixedFontStyle={true}>
                  Close
                </Text>
              </Button>
            </Row>
          </ModalFooter>
        </View>
      </ModalContainer>
    </Modal>
  );
};
